import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Chip, Collapse, FormControl, Icon, TextField } from '@mui/material';
import { AutoComplete } from '../Autocomplete';
import { get, size, uniqBy, sortBy, first, noop } from '../../lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useStyles } from '../form/styles';
import { tradeStyles } from './styles';
import { SimpleSort } from '../sortable-list/simple';
import { Loader, useScreen } from '../../styles';
import { customSearch, ensureArray, isIOS } from '../../utils';
import { Row } from '../row';
import { IconButton } from '../Button';
import Modal from '../modal';
import colors from '../../styles/colors';

export const getTradeName = (trade) => {
  if (!trade) {
    return '';
  }
  return trade.name || trade.defaultName;
};

export const TradeSelectComp = forwardRef((props, ref) => {
  const classes = useStyles({ showPosition: false });
  const styles = tradeStyles({ isIOS: isIOS() });

  const [selected, setSelected] = useState(props.value || []);
  const [data, setData] = useState([]);
  const [checklist, setchecklist] = useState();
  const setByDefault = useRef(false);

  const sortable = get(props, 'sortable', false);

  const formatData = (d = data) => {
    let all = [];
    const hasChecklistTrades = !!size(get(checklist, 'tradeTypes', []));
    if (hasChecklistTrades) {
      const cname = get(checklist, 'name');
      get(checklist, 'tradeTypes', []).map((t) => {
        all.push({ ...t, groupBy: cname });
      });
    }

    all = [...all, ...d.map((dd) => ({ ...dd, groupBy: hasChecklistTrades ? 'All Types' : undefined }))];

    setData(uniqBy(all, 'id'));
  };

  const trades = useRetrieveData(
    { url: '/trade-types' },
    {
      version: 'v2',
      build: ['tradeTypes'],
      useCache: {
        reload: 10,
      },
      params: {
        filter: { enabled: 1 },
      },
      transformData: (d) => {
        d = d.map((dd) => ({ ...dd, _name: getTradeName(dd).toLowerCase() }));
        return sortBy(d, '_name');
      },
    }
  );

  const clid = get(props, 'checklist.checklistId', get(props, 'checklist.id', null));
  const clist = useRetrieveData(
    { url: `/checklists/${clid}` },
    {
      build: ['checklists'],
      params: {
        include: 'tradeTypes',
      },
      waitFor: clid,
      transformData: (d) => {
        const ch = first(d);
        setchecklist(ch);
        const vals = ensureArray(get(ch, 'tradeTypes.0', []));
        setSelected((s) => {
          const shouldSet = !size(s);
          if (shouldSet) {
            setByDefault.current = true;
          } else {
            setByDefault.current = false;
          }
          return shouldSet ? vals : s;
        });
        props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(vals);
      },
    }
  );

  const clistloading = clid && clist.loading;

  useEffect(() => {
    formatData();
  }, [clid, checklist]);

  useEffect(() => {
    if (clid && setByDefault.current) {
      setSelected([]);
    }
  }, [clid]);

  useEffect(() => {
    formatData(trades.data);
  }, [trades.data]);

  useEffect(() => {
    props.onChange && props.onChange(selected);
  }, [selected]);

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    setByDefault.current = false;
  };

  const onSort = (result) => {
    setSelected(result.map((r) => ({ ...r, content: undefined })));
  };

  const renderChip = (s, i) => {
    return <Chip size="small" icon={sortable ? <Icon>drag_indicator</Icon> : undefined} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  };

  return (
    <FormControl variant="standard" className={styles.formControl}>
      <Collapse in={!!size(selected)}>
        <div className={styles.chipContainer}>
          {sortable && <SimpleSort onSort={onSort} items={selected.map((s, i) => ({ ...s, content: renderChip(s, i) }))} />}
          {!sortable && selected.map(renderChip)}
        </div>
      </Collapse>

      <div className={!props.disabled && classes.formWrap}>
        {clistloading && <Loader />}
        {!clistloading && (
          <AutoComplete
            filterOptions={(o, s) => customSearch(o, s, { key: getTradeName })}
            classes={
              props.disabled
                ? {
                    groupLabel: classes.primaryColor,
                    tag: classes.userSelectChip,
                    endAdornment: classes.collapsedScreen,
                  }
                : {
                    groupLabel: classes.primaryColor,
                  }
            }
            topOffset={-180}
            variant="standard"
            multiple={get(props, 'multiple', true)}
            options={data}
            groupBy={(opt) => opt.groupBy}
            getOptionLabel={getTradeName}
            disableCloseOnSelect
            disableClearable
            openOnFocus
            renderInput={(params) => <TextField variant="standard" {...props} onChange={noop} label={undefined} {...params} />}
            renderTags={() => null}
            getOptionSelected={(option, value) => option.id === value.id}
            {...props}
            value={selected || []}
            label={undefined}
            onChange={(e, vals) => {
              const upd = uniqBy(vals, 'id');
              setSelected(upd);
              setByDefault.current = false;
            }}
          />
        )}
      </div>
    </FormControl>
  );
});

export const TradeSelect = (props) => {
  const [selected, setSelected] = useState(props.value || []);
  const [original, setOriginal] = useState(props.value || []);
  const [state, setState] = useState({
    open: false,
  });

  const { isMobileXS } = useScreen();

  useEffect(() => {
    setSelected(props.value || []);
    setOriginal(props.value || []);
  }, [props.value]);

  const styles = tradeStyles();

  const highlightOriginals = get(props, 'highlightOriginals', false);

  const areEqual = highlightOriginals
    ? (selected || []).map((s) => s.id).toString() ==
      get(props, 'compare', original)
        .map((o) => o.id)
        .toString()
    : false;

  const toggleModal = () => {
    setState((s) => ({ ...s, open: !s.open }));
  };

  const onChange = (sel) => {
    setSelected(sel);
    props.onChange && props.onChange(sel);
  };

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    props.onChange && props.onChange(sel);
  };

  const renderChip = (s, i) => {
    return <Chip size="small" style={areEqual && highlightOriginals ? { opacity: 0.6 } : { opacity: 1.0 }} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  };

  const onChecklistTradesLoaded = (c) => {
    setOriginal(c);
    if (!size(selected)) {
      onChange(c);
    }
    props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(c);
  };

  const type = get(props, 'type', 'default');

  if (type === 'inlineModal') {
    return (
      <div style={props.style} className={styles.inlineModal}>
        <Row>
          <Row className={styles.chipContainer}>{selected.map(renderChip)}</Row>
          <IconButton size="small" icon="add" onClick={toggleModal} bgColor={colors.blue} />
        </Row>

        <Modal fullHeight={isMobileXS} fullWidth={true} maxWidth={'sm'} open={state.open} onClose={toggleModal} title={props.modalTitle || 'Add Trade Types'}>
          <div style={{ minHeight: 600 }}>{state.open && <TradeSelectComp autoFocus {...props} value={selected} onChange={onChange} placeholder={'Select Trade Types...'} />}</div>
        </Modal>

        <div style={{ display: 'none' }}>
          <TradeSelectComp {...props} onChange={noop} onChecklistTradesLoaded={onChecklistTradesLoaded} placeholder={'Select Trade Types...'} />
        </div>
      </div>
    );
  }

  return <TradeSelectComp {...props} placeholder={'Select Trade Types...'} />;
};
